import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Shield, AlertTriangle, CheckCircle, Eye, Heart, Phone, MapPin } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const SafetyTips = ({ onBack }) => {
  const safetyCategories = [
    {
      title: 'Online Safety',
      icon: Shield,
      color: 'text-blue-500',
      tips: [
        'Never share personal information like your address, workplace, or financial details',
        'Use FlameLink\'s messaging system until you feel comfortable sharing your phone number',
        'Be cautious of users who ask for money or seem too good to be true',
        'Trust your instincts - if something feels off, it probably is',
        'Keep your social media profiles private until you know someone well'
      ]
    },
    {
      title: 'Meeting in Person',
      icon: MapPin,
      color: 'text-green-500',
      tips: [
        'Always meet in a public place for first dates (coffee shops, restaurants, parks)',
        'Tell a friend or family member where you\'re going and who you\'re meeting',
        'Plan your own transportation - don\'t rely on your date for rides',
        'Stay sober and alert during first meetings',
        'If you feel uncomfortable, it\'s okay to leave at any time'
      ]
    },
    {
      title: 'Red Flags to Watch For',
      icon: AlertTriangle,
      color: 'text-red-500',
      tips: [
        'Profiles with limited or professional-looking photos only',
        'Users who refuse to video chat or talk on the phone',
        'Someone who pushes for personal information too quickly',
        'Inconsistent stories or details about their life',
        'Aggressive or pushy behavior, especially around meeting up'
      ]
    },
    {
      title: 'Communication Safety',
      icon: Phone,
      color: 'text-purple-500',
      tips: [
        'Use video calls to verify identity before meeting in person',
        'Be wary of users who avoid phone calls or video chats',
        'Report and block users who send inappropriate content',
        'Don\'t feel pressured to respond immediately to messages',
        'Screenshot important conversations if you have concerns'
      ]
    }
  ];

  const emergencyContacts = [
    { service: 'Emergency Services', number: '911', description: 'For immediate emergencies' },
    { service: 'Crisis Text Line', number: 'Text HOME to 741741', description: 'Free 24/7 crisis support' },
    { service: 'National Domestic Violence Hotline', number: '1-800-799-7233', description: 'Support for domestic violence' },
    { service: 'Cyber Crime Reporting', number: 'ic3.gov', description: 'Report online crimes and scams' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
              Safety Tips
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your safety is our top priority. Follow these guidelines to have a secure and positive dating experience.
          </p>
        </div>

        {/* Important Notice */}
        <Card className="p-6 mb-8 bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-orange-500 mt-1 mr-4 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-orange-800 mb-2">Important Safety Reminder</h3>
              <p className="text-orange-700 text-sm">
                While FlameLink works hard to maintain a safe platform, we cannot guarantee the identity or intentions 
                of all users. Always prioritize your safety and trust your instincts when interacting with others.
              </p>
            </div>
          </div>
        </Card>

        {/* Safety Categories */}
        <div className="space-y-8">
          {safetyCategories.map((category, index) => {
            const Icon = category.icon;
            return (
              <Card key={index} className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Icon className={`w-6 h-6 ${category.color} mr-3`} />
                  {category.title}
                </h2>
                <div className="space-y-4">
                  {category.tips.map((tip, tipIndex) => (
                    <div key={tipIndex} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-gray-700 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        {/* FlameLink Safety Features */}
        <Card className="p-8 mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Shield className="w-6 h-6 text-blue-500 mr-3" />
            FlameLink Safety Features
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start">
                <Eye className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Photo Verification</h3>
                  <p className="text-gray-600 text-sm">Verified badge ensures authentic profiles</p>
                </div>
              </div>
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">24/7 Moderation</h3>
                  <p className="text-gray-600 text-sm">Active monitoring of profiles and messages</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-orange-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Report & Block</h3>
                  <p className="text-gray-600 text-sm">Easy reporting tools for inappropriate behavior</p>
                </div>
              </div>
              <div className="flex items-start">
                <Heart className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Safe Messaging</h3>
                  <p className="text-gray-600 text-sm">Secure in-app messaging system</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Emergency Contacts */}
        <Card className="p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Emergency Resources</h2>
          <p className="text-gray-600 mb-6">
            If you ever feel unsafe or need immediate help, don't hesitate to contact these resources:
          </p>
          <div className="grid gap-4">
            {emergencyContacts.map((contact, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-gray-900">{contact.service}</h3>
                  <p className="text-gray-600 text-sm">{contact.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-blue-600">{contact.number}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Report Issues */}
        <div className="text-center mt-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Need to Report Something?</h3>
          <p className="text-gray-600 mb-6">
            If you encounter any safety issues or inappropriate behavior on FlameLink, please report it immediately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.open('mailto:safety@flamelink.com', '_blank')}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white"
            >
              Report Safety Issue
            </Button>
            <Button
              variant="outline"
              onClick={onBack}
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyTips;